import * as Y from 'yjs';
import { useEffect, useMemo } from 'react';
import type { OrgChartCsvMapping } from '../domains/orgchart/interop/OrgChartCsvMapping';
import type { FocusScope } from '../domains/orgchart/models/FocusScope';
import type { OrgCustomFieldDefinition } from '../domains/orgchart/models/customFields';
import type { OrgSecondaryReportsVisibilityMode } from '../domains/orgchart/models/secondaryReportsVisibility';
import { Perspective } from '@/stores/perspectiveStore';

type OrgChartRepositoryMapping = Record<string, string | null> & {
  personId: string | null;
  name: string | null;
  title: string | null;
  department: string | null;
  reportsTo: string | null;
  email: string | null;
};

export interface DiagramSettings {
  perspectiveDefault?: Perspective;
  palette?: {
    culture?: Record<string, string>;
    health?: Record<string, { color: string; icon?: string; propagate?: boolean }>;
  };
  label?: {
    line2Field?: string | null; // e.g., 'location', 'occupation', 'age', null to hide
  };
  workflow?: {
    selectedUseCase?: string | null; // UseCaseType
    assistantRole?: string | null; // GenogramAssistantRole
  };
  orgchart?: {
    customFieldDefinitions?: OrgCustomFieldDefinition[];
    csvImport?: {
      mapping?: OrgChartCsvMapping;
      dataSourceId?: string;
    };
    viewMode?: 'compact' | 'full';
    /** Live/editable presentation preference for people card chrome. */
    personCardVariant?: 'photo' | 'text' | 'card';
    /** Live/editable presentation preference for people card accent color. */
    cardAccent?: string;
    layoutStrategy?: 'auto' | 'top-down' | 'left-right' | 'wrapped';
    maxSiblingsPerRow?: number;
    collapsedNodeIds?: string[];
    repositoryMapping?: Record<string, OrgChartRepositoryMapping>;
    focusScope?: FocusScope;
    asOfDate?: string | null;
    autoLayoutEnabled?: boolean;
    /** Legacy setting kept for backward compatibility while migrating to visibilityMode. */
    showSecondaryReportsOnSelection?: boolean;
    /** Visibility mode for dotted / matrix reporting connectors. */
    secondaryReportsVisibilityMode?: OrgSecondaryReportsVisibilityMode;
    nodeSpacing?: number;
    verticalSpacing?: number;
  };
  process?: {
    enabledFieldPacks?: string[];
    complianceFrameworks?: string[];
    controlPointCategories?: string[];
  };
  network?: {
    enabledFieldPacks?: string[];
    cloudProviders?: string[];
    complianceFrameworks?: string[];
    defaultShapeLibrary?: string;
  };
}

export function useDiagramSettings(doc: Y.Doc | null) {
  const { read, write, ensure } = useMemo(() => {
    const getMap = (): Y.Map<any> | null => {
      if (!doc) return null;
      const m = doc.getMap('diagramSettings');
      return m as Y.Map<any>;
    };

    const readFn = (): DiagramSettings => {
      const m = getMap();
      if (!m) return {};
      const json = m.get('settings');
      if (!json) return {};
      try { return JSON.parse(json as string) as DiagramSettings; } catch { return {}; }
    };

    const writeFn = (settings: DiagramSettings) => {
      const m = getMap();
      if (!m) return;
      m.set('settings', JSON.stringify(settings));
    };

    const ensureFn = () => { getMap(); };

    return { read: readFn, write: writeFn, ensure: ensureFn };
  }, [doc]);

  // Ensure map exists
  useEffect(() => { ensure(); }, [ensure]);

  return { read, write };
}
